import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api } from '../api/client';
import TemplateCard from '../components/TemplateCard';
import CategoryTile from '../components/CategoryTile';
import BackButton from '../components/BackButton';
import TopicMedia from '../components/TopicMedia';
import { trackEvent } from '../lib/track';
import { useT, useLocalField } from '../i18n';

export default function CategoryPage() {
  const t = useT();
  const lf = useLocalField();
  const { slug, sub } = useParams();
  const activeSlug = sub || slug;
  const [category, setCategory] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get(`/api/categories/${activeSlug}`),
      api.get(`/api/templates?category=${activeSlug}`),
    ])
      .then(([cat, tData]) => {
        setCategory(cat);
        setTemplates(tData.templates || []);
        // First-party "most-viewed categories" analytics.
        trackEvent('category_view', { slug: cat.slug, name: cat.nameAr });
      })
      .catch(() => setCategory(null))
      .finally(() => setLoading(false));
  }, [activeSlug]);

  if (loading) return <div className="loader"><div className="loader-spinner" /></div>;
  if (!category) return (
    <div className="error-card">
      <h2>{t.categoryNotFound}</h2>
      <Link to="/" className="submit-btn">{t.backToHome}</Link>
    </div>
  );

  // The catch-all "other" templates (a fear or a milestone step that isn't
  // listed) surface as a "+" tile at the END of the grid rather than as a
  // regular card mixed in with the named ones. Pull it out of the template list
  // so it doesn't also render as a duplicate card below.
  const OTHER_SLUGS = ['fear-of-other', 'milestone-other'];
  const otherTemplate = templates.find((tp) => OTHER_SLUGS.includes(tp.slug));

  // Templates driven by a topic picker (the imagination line) surface their
  // ready-made topics directly as storefront tiles, each deep-linking into the
  // create form with that topic preselected — so the picker isn't hidden one
  // click deeper. Such a template renders as tiles instead of a single card.
  const topicTemplate = templates.find((tp) =>
    tp.personalizationSchema?.fields?.some((f) => f.type === 'topic'));
  const topicField = topicTemplate?.personalizationSchema?.fields
    ?.find((f) => f.type === 'topic');
  const topicOptions = topicField?.options || [];

  const listedTemplates = templates.filter(
    (tp) => !OTHER_SLUGS.includes(tp.slug) && tp !== topicTemplate);

  // In a topic-driven category, the remaining named templates (e.g. the
  // World-Cup hero inside imaginary stories) surface as tiles BEFORE the
  // ready-made topics — they're the page's headline stories — deep-linking
  // straight into their own create form. Without a topic template they keep
  // rendering as regular cards below.
  const tileTemplates = topicTemplate ? listedTemplates : [];
  const cardTemplates = topicTemplate ? [] : listedTemplates;

  return (
    <div className="category-page">
      <BackButton />
      <h1 className="page-title">{lf(category, 'nameAr')}</h1>
      {lf(category, 'descriptionAr') && <p className="page-desc">{lf(category, 'descriptionAr')}</p>}

      {category.children?.length > 0 && (
        <div className="category-grid" style={{ marginBottom: 32 }}>
          {category.children.map((c) => <CategoryTile key={c._id} category={c} parentSlug={slug} />)}
        </div>
      )}

      {topicOptions.length > 0 && (
        <div className="topic-grid topic-grid-storefront" style={{ marginBottom: 32 }}>
          {tileTemplates.map((tp) => (
            <Link
              key={tp.slug}
              to={`/create/${tp.slug}`}
              className="topic-chip"
            >
              <TopicMedia image={tp.coverImageUrl} emoji="⭐" />
              <span className="topic-chip-label">{lf(tp, 'nameAr')}</span>
            </Link>
          ))}
          {topicOptions.map((opt) => (
            <Link
              key={opt.value}
              to={`/create/${topicTemplate.slug}?topic=${encodeURIComponent(opt.value)}`}
              className="topic-chip"
            >
              <TopicMedia image={opt.image} emoji={opt.emoji} />
              <span className="topic-chip-label">{lf(opt, 'labelAr')}</span>
            </Link>
          ))}
          <Link
            to={`/create/${topicTemplate.slug}?topic=__other__`}
            className="topic-chip topic-chip-other"
          >
            <span className="topic-chip-media">
              <span className="topic-chip-emoji" aria-hidden="true">{t.topicOtherEmoji}</span>
            </span>
            <span className="topic-chip-label">{t.topicOther}</span>
          </Link>
        </div>
      )}

      {cardTemplates.length > 0 || otherTemplate ? (
        <div className="template-grid">
          {cardTemplates.map((tp) => <TemplateCard key={tp._id} template={tp} />)}
          {/* The "+" catch-all ("my step/fear isn't listed") always closes the
              grid as the LAST item, after every named story. */}
          {otherTemplate && <TemplateCard key={otherTemplate._id} template={otherTemplate} />}
        </div>
      ) : topicOptions.length === 0 ? (
        <p className="empty-state">{t.noTemplatesInCategory}</p>
      ) : null}
    </div>
  );
}
